import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import Meta from '../components/Meta';
import {
  IOS_URL,
  ANDROID_URL,
  appStoreBadgeUrl,
  appStoreBadgeAlt,
  googlePlayBadgeUrl,
  googlePlayBadgeAlt,
  APP_STORE_BADGE_STYLE,
  GOOGLE_PLAY_BADGE_STYLE,
} from '../constants/storeLinks';

function FloatingKanji({ char, style }) {
  return (
    <span
      className="absolute select-none pointer-events-none font-bold text-[#13C5C5] opacity-[0.07]"
      style={style}
    >
      {char}
    </span>
  );
}

function AppleLogo({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M16.365 1.43c0 1.14-.493 2.27-1.177 3.08-.744.9-1.99 1.57-2.987 1.57-.12 0-.23-.02-.3-.03-.01-.06-.04-.22-.04-.39 0-1.15.572-2.27 1.206-2.98.804-.94 2.142-1.64 3.248-1.68.03.13.05.28.05.43zM21 17.34c-.39.91-.83 1.76-1.41 2.6-.79 1.16-1.91 2.6-3.31 2.62-1.24.02-1.56-.81-3.24-.81-1.68 0-2.04.81-3.21.83-1.34.04-2.36-1.25-3.16-2.41-2.27-3.31-2.51-7.18-1.11-9.27.99-1.49 2.55-2.36 4.02-2.36 1.49 0 2.43.82 3.66.82 1.2 0 1.93-.82 3.66-.82 1.31 0 2.7.71 3.69 1.94-3.24 1.78-2.71 6.41.41 7.86z" />
    </svg>
  );
}

function AndroidLogo({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="#3DDC84" aria-hidden="true">
      <path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993.0001.5511-.4482.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4482.9993.9993 0 .5511-.4483.9997-.9993.9997m11.4045-6.02l1.9973-3.4592a.416.416 0 00-.1521-.5676.416.416 0 00-.5676.1521l-2.0223 3.503C15.5902 8.2439 13.8533 7.8508 12 7.8508s-3.5902.3931-5.1367 1.0989L4.841 5.4467a.4161.4161 0 00-.5677-.1521.4157.4157 0 00-.1521.5676l1.9973 3.4592C2.6889 11.1867.3432 14.6589 0 18.7508h24c-.3432-4.0919-2.6889-7.5641-6.1185-9.4294" />
    </svg>
  );
}

function PlatformCard({
  title, subtitle, storeUrl, badgeSrc, badgeAlt, Logo, accent, badgeStyle, delay = 0,
}) {
  return (
    <motion.a
      href={storeUrl}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay, ease: 'easeOut' }}
      whileHover={{ y: -4 }}
      className="group relative bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl border border-[#13C5C5]/15 flex flex-col transition-shadow duration-200"
      style={{ transitionProperty: 'transform, box-shadow' }}
    >
      <div className="h-1.5 w-full bg-gradient-to-r from-[#13C5C5] to-[#b13034]" />

      <div className="flex flex-col items-center px-8 pt-10 pb-8 gap-6 flex-1">
        <div
          className="w-20 h-20 rounded-2xl flex items-center justify-center"
          style={{ background: accent }}
        >
          <Logo className="w-12 h-12" />
        </div>

        <div className="text-center">
          <h3 className="font-rubik font-bold text-2xl text-gray-900">{title}</h3>
          <p className="text-gray-500 text-sm mt-1">{subtitle}</p>
        </div>

        <span className="inline-block">
          <img src={badgeSrc} alt={badgeAlt} style={badgeStyle} />
        </span>
      </div>
    </motion.a>
  );
}

export default function Download() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const appleBadge = appStoreBadgeUrl(lang);
  const playBadge = googlePlayBadgeUrl(lang);

  const kanjiDecorations = [
    { char: '語', style: { top: '8%',  left: '4%',  fontSize: '7rem' } },
    { char: '学', style: { top: '20%', right: '3%', fontSize: '9rem' } },
    { char: '猫', style: { bottom: '22%', left: '6%', fontSize: '6rem' } },
    { char: '日', style: { bottom: '10%', right: '5%', fontSize: '8rem' } },
    { char: '本', style: { top: '55%', left: '1%', fontSize: '5rem' } },
    { char: '力', style: { top: '42%', right: '2%', fontSize: '6rem' } },
  ];

  const features = [
    { icon: '🧠', label: t('download.features.srs.label'),    desc: t('download.features.srs.desc') },
    { icon: '🔥', label: t('download.features.streaks.label'), desc: t('download.features.streaks.desc') },
    { icon: '✨', label: t('download.features.offline.label'), desc: t('download.features.offline.desc') },
  ];

  return (
    <div
      className="min-h-screen relative overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #e8fdf5 0%, #f0faff 100%)' }}
    >
      <Meta titleKey="download.meta.title" descKey="download.meta.description" />

      {kanjiDecorations.map((k, i) => (
        <FloatingKanji key={i} char={k.char} style={k.style} />
      ))}

      <div
        className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: 'radial-gradient(circle at 70% 20%, #13C5C522 0%, transparent 70%)' }}
      />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 relative z-10">

        {/* ── HERO ROW ── */}
        <div className="flex flex-col lg:flex-row items-center gap-12 mb-20">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="flex-1 text-center lg:text-left"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-[#13C5C5]/15 text-[#0a8f8f] font-semibold text-sm rounded-full px-4 py-1.5 mb-6 border border-[#13C5C5]/30"
            >
              <span className="w-2 h-2 rounded-full bg-[#13C5C5] animate-pulse" />
              {t('download.pill')}
            </motion.div>

            <h1
              className="font-rubik font-bold text-[#b13034] leading-tight mb-5"
              style={{ fontSize: 'clamp(2.2rem, 5vw, 3.8rem)' }}
            >
              {t('download.hero.line1')}<br />
              <span className="text-[#13C5C5]">{t('download.hero.line2')}</span>
            </h1>

            <p className="text-gray-600 text-lg md:text-xl mb-10 max-w-md mx-auto lg:mx-0">
              {t('download.hero.desc')}
            </p>

          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30, rotate: 3 }}
            animate={{ opacity: 1, x: 0, rotate: 0 }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="flex-shrink-0 relative"
          >
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#13C5C5]/20 to-[#b13034]/10 blur-3xl scale-110" />
            <img
              src="/images/nekochan-art-transparent.png"
              alt={t('download.mascotAlt')}
              className="relative w-64 md:w-80 lg:w-96 drop-shadow-2xl select-none"
              draggable="false"
            />
          </motion.div>
        </div>

        {/* ── PLATFORM CARDS ── */}
        <div className="mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-center font-rubik font-bold text-2xl md:text-3xl text-gray-800 mb-10"
          >
            {t('download.choosePlatform')}
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
            <PlatformCard
              title={t('download.cards.apple.title')}
              subtitle={t('download.cards.apple.subtitle')}
              storeUrl={IOS_URL}
              badgeSrc={appleBadge}
              badgeAlt={appStoreBadgeAlt(lang)}
              badgeStyle={APP_STORE_BADGE_STYLE}
              Logo={({ className }) => <AppleLogo className={`${className} text-gray-800`} />}
              accent="linear-gradient(135deg, #f4f4f5 0%, #e4e4e7 100%)"
              delay={0.4}
            />
            <PlatformCard
              title={t('download.cards.android.title')}
              subtitle={t('download.cards.android.subtitle')}
              storeUrl={ANDROID_URL}
              badgeSrc={playBadge}
              badgeAlt={googlePlayBadgeAlt(lang)}
              badgeStyle={GOOGLE_PLAY_BADGE_STYLE}
              Logo={AndroidLogo}
              accent="linear-gradient(135deg, #e8fdf5 0%, #d1fadf 100%)"
              delay={0.55}
            />
          </div>
        </div>

        {/* ── FEATURES STRIP ── */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="rounded-3xl bg-gradient-to-r from-[#13C5C5] to-[#0fa8a8] p-8 md:p-10 text-white text-center"
        >
          <h3 className="font-rubik font-bold text-2xl mb-6">{t('download.featuresHeading')}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {features.map(({ icon, label, desc }) => (
              <div key={label} className="flex flex-col items-center gap-2">
                <span className="text-4xl">{icon}</span>
                <span className="font-semibold text-lg">{label}</span>
                <span className="text-white/75 text-sm">{desc}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
